import type { paths } from '../../types/authSchema';
import { getUserLocale } from '@pocapan/shared/utils/getUserLocale';
import { authApiClient, resolveAuthApiErrorMessage } from './authApiClient';
import type { SocialProvider } from './SocialLoginMethods';

export interface SocialLoginCredentialInput {
  idToken?: string;
  identityToken?: string;
  accessToken?: string;
  authorizationCode?: string;
  oauthState?: string;
}

export interface SocialLoginRequest extends SocialLoginCredentialInput {
  provider: SocialProvider;
  locale?: string;
  isPromotionAgreed?: boolean;
  redirectUri?: string;
}

export interface SocialCandidateEmail {
  email: string;
  maskedEmail: string;
}

export interface SocialLoginRequestVerificationCodeRequest {
  socialSessionToken: string;
  selectedEmail: string;
}

export interface SocialLoginVerifyRequest {
  socialSessionToken: string;
  verificationCode: string;
  locale?: string;
}

export interface SocialLoginCreateAccountRequest {
  socialSessionToken: string;
  locale?: string;
  isPromotionAgreed?: boolean;
}

type SocialLoginApiRequestBody = NonNullable<
  NonNullable<
    paths['/api/v1/auth/social_login']['post']['requestBody']
  >['content']['application/json']
>;

type SocialLoginApiSuccessResponse =
  paths['/api/v1/auth/social_login']['post']['responses']['200']['content']['application/json'];

type SocialLoginApiNeedVerificationResponse =
  paths['/api/v1/auth/social_login']['post']['responses']['403']['content']['application/json'];

type SocialLoginRequestVerificationCodeApiRequestBody = NonNullable<
  NonNullable<
    paths['/api/v1/auth/social_login/request_verification_code']['post']['requestBody']
  >['content']['application/json']
>;

type SocialLoginRequestVerificationCodeApiSuccessResponse =
  paths['/api/v1/auth/social_login/request_verification_code']['post']['responses']['200']['content']['application/json'];

type SocialLoginVerifyApiRequestBody = NonNullable<
  NonNullable<
    paths['/api/v1/auth/social_login/verify']['post']['requestBody']
  >['content']['application/json']
>;

type SocialLoginVerifyApiSuccessResponse =
  paths['/api/v1/auth/social_login/verify']['post']['responses']['200']['content']['application/json'];

type SocialLoginCreateAccountApiRequestBody = NonNullable<
  NonNullable<
    paths['/api/v1/auth/social_login/create_account']['post']['requestBody']
  >['content']['application/json']
>;

type SocialLoginCreateAccountApiSuccessResponse =
  paths['/api/v1/auth/social_login/create_account']['post']['responses']['201']['content']['application/json'];

type SocialFallbackErrorResponse = {
  success: false;
  error: {
    message: string;
  };
};

export type SocialAuthSuccessResponse =
  | SocialLoginApiSuccessResponse
  | SocialLoginVerifyApiSuccessResponse
  | SocialLoginCreateAccountApiSuccessResponse;

export type SocialLoginResponse =
  | SocialAuthSuccessResponse
  | SocialLoginApiNeedVerificationResponse
  | SocialFallbackErrorResponse;

export type SocialLoginRequestVerificationCodeResponse =
  | SocialLoginRequestVerificationCodeApiSuccessResponse
  | SocialFallbackErrorResponse;

export type SocialLoginVerifyResponse = SocialAuthSuccessResponse | SocialFallbackErrorResponse;

export type SocialLoginCreateAccountResponse =
  | SocialAuthSuccessResponse
  | SocialFallbackErrorResponse;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function extractSocialPayload(value: unknown): Record<string, unknown> | null {
  const record = asRecord(value);
  if (!record) return null;

  const data = asRecord(record.data);
  if (data) {
    return data;
  }

  const body = asRecord(record.body);
  if (body) {
    return body;
  }

  return record;
}

function getBooleanValue(target: Record<string, unknown>, key: string): boolean | null {
  const value = target[key];
  if (typeof value === 'boolean') return value;
  return null;
}

function getStringValue(target: Record<string, unknown>, key: string): string | null {
  const value = target[key];
  if (typeof value === 'string' && value.trim() !== '') return value;
  return null;
}

function findStringByKeys(target: Record<string, unknown>, keys: readonly string[]) {
  for (const key of keys) {
    const value = getStringValue(target, key);
    if (value) return value;
  }
  return null;
}

async function extractResponsePayload(response?: Response) {
  if (!response) return null;
  try {
    const payload = await response.clone().json();
    return extractSocialPayload(payload);
  } catch (_error) {
    return null;
  }
}

function createFallbackErrorResponse(
  error: unknown,
  fallbackMessage: string
): SocialFallbackErrorResponse {
  return {
    success: false,
    error: {
      message: resolveAuthApiErrorMessage(error, fallbackMessage)
    }
  };
}

function getErrorRecord(response: Record<string, unknown>) {
  const record = asRecord(response);
  if (!record) return null;
  return asRecord(record.error);
}

function resolveSocialLoginCredential(request: SocialLoginRequest): {
  credential_type: SocialLoginApiRequestBody['credential_type'];
  credential: string;
} | null {
  const idTokenValue = (request.idToken ?? request.identityToken)?.trim();
  if (request.provider === 'apple') {
    if (idTokenValue) {
      return { credential_type: 'id_token', credential: idTokenValue };
    }
    const appleCode = request.authorizationCode?.trim();
    if (appleCode) {
      return { credential_type: 'authorization_code', credential: appleCode };
    }
    const appleAccess = request.accessToken?.trim();
    if (appleAccess) {
      return { credential_type: 'access_token', credential: appleAccess };
    }
    return null;
  }
  const code = request.authorizationCode?.trim();

  if (code) {
    return { credential_type: 'authorization_code', credential: code };
  }

  if (idTokenValue) {
    return { credential_type: 'id_token', credential: idTokenValue };
  }
  const accessToken = request.accessToken?.trim();
  if (accessToken) {
    return { credential_type: 'access_token', credential: accessToken };
  }
  return null;
}

export async function socialLogin(request: SocialLoginRequest): Promise<SocialLoginResponse> {
  const locale = request.locale ?? getUserLocale();
  const resolved = resolveSocialLoginCredential(request);
  if (!resolved) {
    return createFallbackErrorResponse(
      new Error('Missing social credential'),
      'Social login failed. Please try again.'
    );
  }

  const body: SocialLoginApiRequestBody & Record<string, unknown> = {
    provider: request.provider as SocialLoginApiRequestBody['provider'],
    credential_type: resolved.credential_type,
    credential: resolved.credential,
    ...(locale ? { locale } : {}),
    ...(request.redirectUri ? { redirect_uri: request.redirectUri } : {}),
    ...(request.oauthState ? { state: request.oauthState } : {}),
    is_promotion_agreed: request.isPromotionAgreed
  };

  try {
    const { data, error, response } = await authApiClient.POST('/api/v1/auth/social_login', {
      body: body as SocialLoginApiRequestBody
    });

    const dataPayload = extractSocialPayload(data);
    if (dataPayload) {
      return dataPayload as SocialLoginResponse;
    }

    const rawResponsePayload = await extractResponsePayload(response);
    if (rawResponsePayload) {
      return rawResponsePayload as SocialLoginResponse;
    }

    const errorPayload = extractSocialPayload(error);
    if (errorPayload) {
      return errorPayload as SocialLoginResponse;
    }

    return createFallbackErrorResponse(error, 'Social login failed. Please try again.');
  } catch (error) {
    return createFallbackErrorResponse(error, 'Social login failed. Please try again.');
  }
}

export async function socialLoginRequestVerificationCode({
  socialSessionToken,
  selectedEmail
}: SocialLoginRequestVerificationCodeRequest): Promise<SocialLoginRequestVerificationCodeResponse> {
  const body: SocialLoginRequestVerificationCodeApiRequestBody = {
    social_session_token: socialSessionToken,
    selected_email: selectedEmail
  };

  try {
    const { data, error, response } = await authApiClient.POST(
      '/api/v1/auth/social_login/request_verification_code',
      {
        body
      }
    );

    if (data) {
      return data;
    }

    const rawResponsePayload = await extractResponsePayload(response);
    if (rawResponsePayload) {
      return rawResponsePayload as SocialLoginRequestVerificationCodeResponse;
    }

    const errorPayload = extractSocialPayload(error);
    if (errorPayload) {
      return errorPayload as SocialLoginRequestVerificationCodeResponse;
    }

    return createFallbackErrorResponse(
      error,
      'Failed to request social verification code. Please try again.'
    );
  } catch (error) {
    return createFallbackErrorResponse(
      error,
      'Failed to request social verification code. Please try again.'
    );
  }
}

export async function socialLoginVerify({
  socialSessionToken,
  verificationCode,
  locale
}: SocialLoginVerifyRequest): Promise<SocialLoginVerifyResponse> {
  const resolvedLocale = locale ?? getUserLocale();
  const body: SocialLoginVerifyApiRequestBody = {
    social_session_token: socialSessionToken,
    verification_code: verificationCode,
    ...(resolvedLocale ? { locale: resolvedLocale } : {})
  };

  try {
    const { data, error, response } = await authApiClient.POST('/api/v1/auth/social_login/verify', {
      body
    });

    if (data) {
      return data;
    }

    const rawResponsePayload = await extractResponsePayload(response);
    if (rawResponsePayload) {
      return rawResponsePayload as SocialLoginVerifyResponse;
    }

    const errorPayload = extractSocialPayload(error);
    if (errorPayload) {
      return errorPayload as SocialLoginVerifyResponse;
    }

    return createFallbackErrorResponse(
      error,
      'Social login verification failed. Please try again.'
    );
  } catch (error) {
    return createFallbackErrorResponse(
      error,
      'Social login verification failed. Please try again.'
    );
  }
}

export async function socialLoginCreateAccount({
  socialSessionToken,
  locale,
  isPromotionAgreed
}: SocialLoginCreateAccountRequest): Promise<SocialLoginCreateAccountResponse> {
  const resolvedLocale = locale ?? getUserLocale();
  const body: SocialLoginCreateAccountApiRequestBody = {
    social_session_token: socialSessionToken,
    ...(resolvedLocale ? { locale: resolvedLocale } : {}),
    is_promotion_agreed: isPromotionAgreed
  };

  try {
    const { data, error, response } = await authApiClient.POST(
      '/api/v1/auth/social_login/create_account',
      {
        body
      }
    );

    if (data) {
      return data;
    }

    const rawResponsePayload = await extractResponsePayload(response);
    if (rawResponsePayload) {
      return rawResponsePayload as SocialLoginCreateAccountResponse;
    }

    const errorPayload = extractSocialPayload(error);
    if (errorPayload) {
      return errorPayload as SocialLoginCreateAccountResponse;
    }

    return createFallbackErrorResponse(error, 'Failed to create account with social login.');
  } catch (error) {
    return createFallbackErrorResponse(error, 'Failed to create account with social login.');
  }
}

export function isSocialLoginSuccess(response: Record<string, unknown>) {
  const record = asRecord(response);
  if (!record) return false;

  const success = getBooleanValue(record, 'success');
  const jwt = getStringValue(record, 'jwt');
  return success === true && typeof jwt === 'string' && jwt.length > 0;
}

export function shouldRequireEmailVerification(response: Record<string, unknown>) {
  const error = getErrorRecord(response);
  if (!error) return false;
  return getBooleanValue(error, 'verificationRequired') === true;
}

export function getSocialSessionToken(response: Record<string, unknown>) {
  const error = getErrorRecord(response);
  if (!error) return null;
  return getStringValue(error, 'socialSessionToken');
}

export function canContinueWithNewSocialAccount(response: Record<string, unknown>) {
  const error = getErrorRecord(response);
  if (!error) return false;
  return getBooleanValue(error, 'continueWithNewAccountAvailable') === true;
}

export function getSocialCandidateEmails(
  response: Record<string, unknown>
): SocialCandidateEmail[] {
  const error = getErrorRecord(response);
  if (!error) return [];

  const candidateEmails = error.candidateEmails;
  if (!Array.isArray(candidateEmails)) return [];

  return candidateEmails
    .map(asRecord)
    .filter((candidate): candidate is Record<string, unknown> => candidate !== null)
    .map((candidate) => {
      const email = getStringValue(candidate, 'email');
      const maskedEmail = getStringValue(candidate, 'maskedEmail');
      if (!email || !maskedEmail) return null;
      return { email, maskedEmail };
    })
    .filter((candidate): candidate is SocialCandidateEmail => candidate !== null);
}

export function getSocialLoginErrorMessage(response: Record<string, unknown>) {
  const topMessage = findStringByKeys(response, ['errorMessage', 'message']);
  if (topMessage) return topMessage;

  const error = getErrorRecord(response);
  if (!error) return null;

  return findStringByKeys(error, ['message', 'detail', 'errorMessage']);
}
